import React, { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Trash2, RefreshCw, Database, ShieldAlert, RotateCcw, FileArchive } from "lucide-react";

function fmtBytes(n) {
  if (!n || n < 1024) return `${n || 0} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-CO"); } catch { return iso; }
}

export default function AdminMaintenancePage() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backups, setBackups] = useState([]);
  const [loadingBackups, setLoadingBackups] = useState(true);

  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [skipBackup, setSkipBackup] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreConfirm, setRestoreConfirm] = useState("");
  const [restoring, setRestoring] = useState(false);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/cleanup/preview");
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando preview");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadBackups = useCallback(async () => {
    setLoadingBackups(true);
    try {
      const { data } = await api.get("/admin/cleanup/backups");
      setBackups(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error cargando backups");
    } finally {
      setLoadingBackups(false);
    }
  }, []);

  useEffect(() => { loadPreview(); loadBackups(); }, [loadPreview, loadBackups]);

  const executeCleanup = async () => {
    if (confirmation !== "BORRAR") {
      toast.error('Debes escribir exactamente "BORRAR"');
      return;
    }
    setExecuting(true);
    try {
      const { data } = await api.post("/admin/cleanup/test-data", {
        confirmation: "BORRAR",
        skip_backup: skipBackup,
      });
      setLastResult(data);
      toast.success("Limpieza completada");
      setCleanupOpen(false);
      setConfirmation("");
      loadPreview();
      loadBackups();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error ejecutando limpieza");
    } finally {
      setExecuting(false);
    }
  };

  const executeRestore = async () => {
    if (restoreConfirm !== "RESTAURAR") {
      toast.error('Debes escribir exactamente "RESTAURAR"');
      return;
    }
    setRestoring(true);
    try {
      await api.post("/admin/cleanup/restore", {
        backup_id: restoreTarget.id,
        confirmation: "RESTAURAR",
      });
      toast.success(`Restauración completada desde ${restoreTarget.id}`);
      setRestoreTarget(null);
      setRestoreConfirm("");
      loadPreview();
      loadBackups();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error restaurando");
    } finally {
      setRestoring(false);
    }
  };

  const usersCount = preview?.preview?.users || {};
  const txCount = preview?.preview?.transactional_to_clear || {};
  const txTotal = preview?.preview?.transactional_total || 0;
  const preserved = preview?.preview?.preserved || {};

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="font-serif text-3xl text-gray-900 mb-1 flex items-center gap-2">
          <ShieldAlert className="w-7 h-7 text-brand-burgundy" />
          Mantenimiento
        </h1>
        <p className="text-sm text-gray-600">Limpieza de datos de prueba y gestión de backups locales.</p>
      </div>

      {/* Preview de qué se borraría */}
      <div className="border border-gray-200 bg-white rounded-sm p-6 mb-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="font-serif text-xl text-gray-900 mb-1 flex items-center gap-2">
              <Database className="w-5 h-5 text-brand-burgundy" />
              Estado actual de la base de datos
            </h2>
            <p className="text-xs text-gray-500">Vista previa sin tocar datos.</p>
          </div>
          <Button variant="outline" size="sm" onClick={loadPreview} disabled={loading} data-testid="reload-preview">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refrescar
          </Button>
        </div>

        {loading ? (
          <div className="text-center text-gray-500 py-6">Cargando…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Stat label="Usuarios reales (preservados)" value={usersCount.protected_real || 0} color="text-emerald-700" />
            <Stat label="Usuarios de prueba (a borrar)" value={usersCount.test_to_delete || 0} color="text-red-700" />
            <Stat label="Docs transaccionales (a borrar)" value={txTotal} color="text-red-700" />
          </div>
        )}

        {preview && (
          <details className="mt-4">
            <summary className="text-sm text-gray-700 cursor-pointer hover:text-brand-burgundy">Ver detalle por colección</summary>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
              {Object.entries(txCount).map(([col, n]) => (
                <div key={col} className="flex justify-between border-b border-gray-100 py-1">
                  <code className="text-gray-700">{col}</code>
                  <span className={`font-medium ${n > 0 ? "text-red-700" : "text-gray-400"}`}>{n}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 text-xs text-emerald-700">
              <strong>Preservadas:</strong>{" "}
              {Object.entries(preserved).map(([col, n]) => `${col} (${n})`).join(" · ")}
            </div>
            {(usersCount.test_emails_sample || []).length > 0 && (
              <div className="mt-3 text-xs text-gray-600">
                <strong>Muestra de emails a borrar:</strong>{" "}
                <span className="text-gray-500">{usersCount.test_emails_sample.slice(0, 8).join(", ")}{usersCount.test_emails_sample.length > 8 ? "…" : ""}</span>
              </div>
            )}
          </details>
        )}

        <div className="mt-6 flex flex-wrap gap-3">
          <Button
            onClick={() => setCleanupOpen(true)}
            disabled={loading || (txTotal === 0 && (usersCount.test_to_delete || 0) === 0)}
            className="bg-red-700 hover:bg-red-800 text-white"
            data-testid="open-cleanup"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Limpiar datos de prueba
          </Button>
          <span className="text-xs text-gray-500 flex items-center">
            Crea backup automático y borra usuarios + datos transaccionales. Config preservada.
          </span>
        </div>
      </div>

      {/* Resultado última limpieza */}
      {lastResult && (
        <div className="border border-emerald-300 bg-emerald-50 rounded-sm p-4 mb-6">
          <h3 className="font-medium text-emerald-900 mb-2">Última limpieza ejecutada</h3>
          <div className="text-sm text-emerald-800 grid grid-cols-2 gap-1">
            <div>Usuarios borrados: <strong>{lastResult.deleted?.users}</strong></div>
            <div>Docs transaccionales: <strong>{lastResult.deleted?.transactional_total}</strong></div>
            <div>Backup creado: <strong>{lastResult.backup?.id || "—"}</strong></div>
            <div>Por: <strong>{lastResult.executed_by}</strong></div>
          </div>
        </div>
      )}

      {/* Backups */}
      <div className="border border-gray-200 bg-white rounded-sm p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="font-serif text-xl text-gray-900 mb-1 flex items-center gap-2">
              <FileArchive className="w-5 h-5 text-brand-burgundy" />
              Backups disponibles
            </h2>
            <p className="text-xs text-gray-500">Restaura el estado completo de la DB desde un snapshot anterior.</p>
          </div>
          <Button variant="outline" size="sm" onClick={loadBackups} disabled={loadingBackups}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loadingBackups ? "animate-spin" : ""}`} /> Refrescar
          </Button>
        </div>

        <div className="border border-gray-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-600">
              <tr>
                <th className="text-left p-3 font-medium">ID</th>
                <th className="text-left p-3 font-medium">Creado</th>
                <th className="text-left p-3 font-medium">Tamaño</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {loadingBackups && backups.length === 0 && (
                <tr><td colSpan={4} className="p-6 text-center text-gray-500">Cargando…</td></tr>
              )}
              {!loadingBackups && backups.length === 0 && (
                <tr><td colSpan={4} className="p-6 text-center text-gray-500">Sin backups locales.</td></tr>
              )}
              {backups.map((b) => (
                <tr key={b.id} className="border-t border-gray-100 hover:bg-gray-50/50">
                  <td className="p-3 font-mono text-xs">{b.id}</td>
                  <td className="p-3 text-gray-700">{fmtDate(b.created_at)}</td>
                  <td className="p-3 text-gray-700">{fmtBytes(b.size_bytes)}</td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="outline" onClick={() => setRestoreTarget(b)} data-testid={`restore-${b.id}`}>
                      <RotateCcw className="w-4 h-4 mr-1" /> Restaurar
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Cleanup */}
      <Dialog open={cleanupOpen} onOpenChange={(o) => !o && !executing && setCleanupOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <ShieldAlert className="w-5 h-5" /> Confirmar limpieza destructiva
            </DialogTitle>
            <DialogDescription>
              Vas a borrar <strong className="text-red-700">{usersCount.test_to_delete || 0} usuarios de prueba</strong>{" "}
              y <strong className="text-red-700">{txTotal} documentos transaccionales</strong>.
              <br /><br />
              Los <strong>4 usuarios reales</strong> y la configuración del sistema se preservan.
              <br /><br />
              Esta acción es <strong>irreversible</strong> (excepto vía backup).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-sm text-gray-700 mb-1 block">
                Escribe <code className="bg-gray-100 px-1.5 py-0.5 text-red-700">BORRAR</code> para confirmar:
              </label>
              <Input
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                placeholder="BORRAR"
                className={confirmation === "BORRAR" ? "border-red-500" : ""}
                data-testid="cleanup-confirmation"
                autoFocus
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input type="checkbox" checked={skipBackup} onChange={(e) => setSkipBackup(e.target.checked)} />
              <span>Omitir backup previo <span className="text-amber-700">(no recomendado)</span></span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCleanupOpen(false)} disabled={executing}>Cancelar</Button>
            <Button
              onClick={executeCleanup}
              disabled={confirmation !== "BORRAR" || executing}
              className="bg-red-700 hover:bg-red-800 text-white"
              data-testid="cleanup-execute"
            >
              {executing ? "Ejecutando…" : "BORRAR ahora"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Restore */}
      <Dialog open={!!restoreTarget} onOpenChange={(o) => !o && !restoring && setRestoreTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-amber-700 flex items-center gap-2">
              <RotateCcw className="w-5 h-5" /> Restaurar backup
            </DialogTitle>
            <DialogDescription>
              Vas a <strong>reemplazar TODA la base de datos</strong> con el contenido del backup{" "}
              <code className="bg-gray-100 px-1.5 py-0.5">{restoreTarget?.id}</code> ({fmtBytes(restoreTarget?.size_bytes || 0)}).
              <br /><br />
              Esta operación es <strong>destructiva</strong>: todo cambio posterior al backup se perderá.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <label className="text-sm text-gray-700 mb-1 block">
              Escribe <code className="bg-gray-100 px-1.5 py-0.5 text-amber-700">RESTAURAR</code> para confirmar:
            </label>
            <Input
              value={restoreConfirm}
              onChange={(e) => setRestoreConfirm(e.target.value)}
              placeholder="RESTAURAR"
              autoFocus
              data-testid="restore-confirmation"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreTarget(null)} disabled={restoring}>Cancelar</Button>
            <Button
              onClick={executeRestore}
              disabled={restoreConfirm !== "RESTAURAR" || restoring}
              className="bg-amber-700 hover:bg-amber-800 text-white"
              data-testid="restore-execute"
            >
              {restoring ? "Restaurando…" : "Restaurar ahora"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="border border-gray-200 rounded-sm p-4">
      <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">{label}</div>
      <div className={`font-serif text-3xl ${color || "text-gray-900"}`}>{value}</div>
    </div>
  );
}
